import { useEffect, useRef, useState } from 'react';
import { Section } from '~/components/layout';
import { cn } from '~/lib/cn';

export interface Quote {
  quote: string;
  name: string;
  role: string;
  /** Optional headshot URL. When missing, the card shows name initials. */
  avatar?: string;
  /** Optional external profile URL (e.g. LinkedIn). Stored for CMS; not shown in the card UI yet. */
  profileUrl?: string;
}

// One "step" = card width (292px) + row gap (20px).
const CARD_STEP = 312;

/** Skip common honorifics so "Md Zubayer Islam" → "ZI", not "MZ". */
const NAME_HONORIFICS = new Set(['md', 'mr', 'mrs', 'ms', 'miss', 'dr', 'prof']);

/** "John Doe" → "JD"; "Md Zubayer Islam" → "ZI"; single name → first 1–2 chars. */
export function quoteInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const significant = words.filter(
    (word) => !NAME_HONORIFICS.has(word.replace(/\./g, '').toLowerCase()),
  );
  const parts = significant.length > 0 ? significant : words;
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

/** Star-rated quote cards (Figma Cards / Business testimonial rows). */
export function QuoteTestimonials({
  title,
  quotes,
  className,
}: {
  title: string;
  quotes: Quote[];
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0 });
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateEdges = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    updateEdges();
  }, [quotes]);

  const step = (dir: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: dir * CARD_STEP, behavior: 'smooth' });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    drag.current = { down: true, startX: e.clientX, startLeft: el.scrollLeft };
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const el = scrollRef.current;
    if (!el || !drag.current.down) return;
    el.scrollLeft = drag.current.startLeft - (e.clientX - drag.current.startX);
  };

  const endDrag = (e: React.PointerEvent) => {
    drag.current.down = false;
    scrollRef.current?.releasePointerCapture?.(e.pointerId);
  };

  return (
    <Section className={className}>
      <div className="flex items-end justify-between gap-6 border-b border-black-6 pb-8">
        <h2 className="max-w-xl text-h3 font-semibold tracking-[-0.03em] text-balance md:text-h2">
          {title}
        </h2>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => step(-1)}
            disabled={atStart}
            aria-label="Previous testimonials"
            className="grid size-12 place-items-center rounded-full bg-grey-50 text-ink transition hover:bg-grey-100 disabled:pointer-events-none disabled:opacity-40">
            <Arrow direction="left" />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            disabled={atEnd}
            aria-label="Next testimonials"
            className="grid size-12 place-items-center rounded-full bg-red-500 text-white transition hover:bg-red-700 disabled:pointer-events-none disabled:opacity-40">
            <Arrow direction="right" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={updateEdges}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="mt-10 flex cursor-grab gap-5 overflow-x-auto select-none [-ms-overflow-style:none] [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden">
        {quotes.map((q, i) => (
          <figure
            key={`${q.name}-${i}`}
            className="flex h-[460px] w-[292px] shrink-0 flex-col gap-5 bg-grey-25 p-5">
            <div className="flex gap-1 text-ink" aria-label="5 out of 5 stars">
              {Array.from({ length: 5 }).map((_, s) => (
                <svg
                  key={s}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-4"
                  style={{ strokeLinejoin: 'round', strokeWidth: 0.5, stroke: 'currentColor' }}>
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ))}
            </div>
            <blockquote className="whitespace-pre-line text-body-m text-grey-900">
              {q.quote}
            </blockquote>
            <figcaption className="mt-auto flex items-center gap-3">
              {q.avatar ? (
                <img
                  src={q.avatar}
                  alt={q.name}
                  className="size-12 shrink-0 rounded-full object-cover"
                  draggable={false}
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="grid size-12 shrink-0 place-items-center rounded-full bg-grey-100 text-body-s font-semibold text-grey-600">
                  {quoteInitials(q.name)}
                </span>
              )}
              <div className="flex flex-col gap-2.5">
                <p className="text-body-m font-semibold text-ink">{q.name}</p>
                <p className="text-body-m text-grey-500">{q.role}</p>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
}

// Inlined from /public/icons/arrow-left.svg so it can inherit `currentColor`
// (dark on the grey button, white on the red button). Rotated 180° for "next".
function Arrow({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-5', direction === 'right' && 'rotate-180')}>
      <path
        d="M7.68115 18.5L2.86084 12.5674L2.3999 12L2.86084 11.4326L7.68115 5.5L10.0005 5.5L4.71924 12L10.0005 18.5L7.68115 18.5Z"
        fill="currentColor"
      />
      <path d="M4 12H21.5" stroke="currentColor" strokeWidth={1.8} />
    </svg>
  );
}
